function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class ScrollPill {
  static selectors = ['.thumbnail-list'];
  static instances = new WeakMap();

  constructor(target) {
    this.target = target;
    this.host = document.createElement('div');
    this.host.className = 'scroll-pill-host scroll-pill-host--rail';

    this.pill = document.createElement('button');
    this.pill.type = 'button';
    this.pill.className = 'scroll-pill';
    this.pill.setAttribute('aria-hidden', 'true');
    this.pill.setAttribute('tabindex', '-1');

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.target.classList.add('scroll-pill-target');
    const parent = this.target.parentNode;
    if (!parent) return;

    parent.insertBefore(this.host, this.target);
    this.host.appendChild(this.target);
    this.host.appendChild(this.pill);

    this.target.addEventListener('scroll', this.onScroll, { passive: true });
    this.pill.addEventListener('pointerdown', this.onPointerDown);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.target);
      this.resizeObserver.observe(this.host);
    }

    this.update();
  }

  onScroll() {
    this.updatePosition();
  }

  onResize() {
    this.update();
  }

  update() {
    if (!this.host.isConnected || !this.target.isConnected) return;

    const hasVerticalScroll = this.target.scrollHeight - this.target.clientHeight > 4;
    this.host.classList.toggle('has-scroll-pill', hasVerticalScroll);
    this.pill.hidden = !hasVerticalScroll;

    if (!hasVerticalScroll) return;

    const pillHeight = this.pill.offsetHeight || 40;
    const maxTravel = Math.max(this.target.clientHeight - pillHeight, 0);
    this.maxTravel = maxTravel;
    this.maxScroll = Math.max(this.target.scrollHeight - this.target.clientHeight, 0);
    this.trackTop = this.target.offsetTop;
    this.updatePosition();
  }

  updatePosition() {
    if (!this.maxScroll && this.maxScroll !== 0) this.update();
    if (!this.host.classList.contains('has-scroll-pill')) return;

    const ratio = this.maxScroll > 0 ? this.target.scrollTop / this.maxScroll : 0;
    const offset = this.trackTop + this.maxTravel * ratio;
    this.pill.style.transform = `translateY(${offset}px)`;
  }

  onPointerDown(event) {
    event.preventDefault();
    this.update();
    this.dragging = true;
    this.pill.classList.add('is-dragging');
    this.pointerId = event.pointerId;
    this.startY = event.clientY;
    this.startScrollTop = this.target.scrollTop;
    this.pill.setPointerCapture(event.pointerId);
    this.pill.addEventListener('pointermove', this.onPointerMove);
    this.pill.addEventListener('pointerup', this.onPointerUp);
    this.pill.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    const deltaY = event.clientY - this.startY;
    const ratio = this.maxTravel > 0 ? deltaY / this.maxTravel : 0;
    this.target.scrollTop = this.startScrollTop + this.maxScroll * ratio;
  }

  onPointerUp(event) {
    this.dragging = false;
    this.pill.classList.remove('is-dragging');
    if (this.pointerId !== undefined) {
      this.pill.releasePointerCapture(this.pointerId);
    }
    this.pill.removeEventListener('pointermove', this.onPointerMove);
    this.pill.removeEventListener('pointerup', this.onPointerUp);
    this.pill.removeEventListener('pointercancel', this.onPointerUp);
  }

  destroy() {
    this.target.removeEventListener('scroll', this.onScroll);
    this.pill.removeEventListener('pointerdown', this.onPointerDown);
    this.resizeObserver?.disconnect();
    this.pill.remove();
    this.target.classList.remove('scroll-pill-target');
    this.host.classList.remove('scroll-pill-host', 'scroll-pill-host--rail', 'has-scroll-pill');
  }

  static init(root = document) {
    this.selectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((target) => {
        if (this.instances.has(target)) return;
        const instance = new ScrollPill(target);
        if (!instance.host) return;
        this.instances.set(target, instance);
      });
    });
  }
}

class PageScrollPill {
  constructor() {
    this.target = document.scrollingElement || document.documentElement;
    this.host = document.createElement('div');
    this.host.className = 'scroll-pill-host scroll-pill-host--page';

    this.pill = document.createElement('button');
    this.pill.type = 'button';
    this.pill.className = 'scroll-pill';
    this.pill.setAttribute('aria-hidden', 'true');
    this.pill.setAttribute('tabindex', '-1');

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.host.appendChild(this.pill);
    document.body.appendChild(this.host);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    this.pill.addEventListener('pointerdown', this.onPointerDown);

    this.update();
  }

  update() {
    const viewportHeight = window.innerHeight;
    const pageHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      this.target.scrollHeight
    );
    this.maxScroll = Math.max(pageHeight - viewportHeight, 0);
    this.maxTravel = Math.max(viewportHeight - (this.pill.offsetHeight || 40) - 16, 0);

    const hasScroll = this.maxScroll > 4;
    this.host.classList.toggle('has-scroll-pill', hasScroll);
    this.pill.hidden = !hasScroll;

    if (hasScroll) this.updatePosition();
  }

  updatePosition() {
    if (this.maxScroll <= 0) return;
    const ratio = window.scrollY / this.maxScroll;
    const offset = 8 + this.maxTravel * ratio;
    this.pill.style.transform = `translateY(${offset}px)`;
  }

  onScroll() {
    this.updatePosition();
  }

  onResize() {
    this.update();
  }

  onPointerDown(event) {
    event.preventDefault();
    this.dragging = true;
    this.pill.classList.add('is-dragging');
    this.pointerId = event.pointerId;
    this.startY = event.clientY;
    this.startScrollY = window.scrollY;
    this.pill.setPointerCapture(event.pointerId);
    this.pill.addEventListener('pointermove', this.onPointerMove);
    this.pill.addEventListener('pointerup', this.onPointerUp);
    this.pill.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    const deltaY = event.clientY - this.startY;
    const ratio = this.maxTravel > 0 ? deltaY / this.maxTravel : 0;
    window.scrollTo({ top: this.startScrollY + this.maxScroll * ratio, behavior: 'auto' });
  }

  onPointerUp() {
    this.dragging = false;
    this.pill.classList.remove('is-dragging');
    if (this.pointerId !== undefined) this.pill.releasePointerCapture(this.pointerId);
    this.pill.removeEventListener('pointermove', this.onPointerMove);
    this.pill.removeEventListener('pointerup', this.onPointerUp);
    this.pill.removeEventListener('pointercancel', this.onPointerUp);
  }
}

function stripTrailingZerosFromMoneyText(value) {
  if (!value || !/[0-9]/.test(value)) return value;
  return value.replace(/([.,])00(?=(?:\s|&nbsp;|[A-Z]{2,4}|COP|USD|EUR|MXN|CLP|ARS|S\/|US\$|COP\$|$))/g, '');
}

const moneyTextSelector = [
  '.price',
  '.price-item',
  '.variant-price',
  '.original-price',
  '.compare-price-custom',
  '.totals__total-value',
  '.cart-item__price-wrapper',
  '.cart-item__old-price',
  '.cart-item__final-price',
  '.variant-item__old-price',
  '.price-per-item',
  '.price-per-item--current',
  '.unit-price',
  '.suscripciones__plan-price',
  '.suscripciones__plan-price-old',
  '.text-blocks__pack-price'
].join(', ');

function normalizeMoneyTextNodes(root = document) {
  const elements = [];
  if (root instanceof Element && root.matches(moneyTextSelector)) elements.push(root);
  root.querySelectorAll?.(moneyTextSelector).forEach((element) => elements.push(element));

  elements.forEach((element) => {
    element.childNodes.forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      const nextValue = stripTrailingZerosFromMoneyText(node.textContent);
      if (nextValue !== node.textContent) node.textContent = nextValue;
    });
  });
}

function initMoneyFormatting(root = document) {
  normalizeMoneyTextNodes(root);

  if (!window.__moneyFormatObserver) {
    const pendingRoots = new Set();
    let formatFrame = null;

    const queueMoneyFormatting = (root) => {
      pendingRoots.add(root);
      if (formatFrame) return;

      formatFrame = requestAnimationFrame(() => {
        pendingRoots.forEach((pendingRoot) => {
          if (pendingRoot === document || pendingRoot.isConnected) normalizeMoneyTextNodes(pendingRoot);
        });
        pendingRoots.clear();
        formatFrame = null;
      });
    };

    window.__moneyFormatObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches(moneyTextSelector) || node.querySelector(moneyTextSelector)) {
            queueMoneyFormatting(node);
          }
        });
        if (mutation.type === 'characterData' && mutation.target.parentElement) {
          const moneyElement = mutation.target.parentElement.closest(moneyTextSelector);
          if (moneyElement) queueMoneyFormatting(moneyElement);
        }
      });
    });

    window.__moneyFormatObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ScrollPill.init(document);
    if (!window.__pageScrollPill) window.__pageScrollPill = new PageScrollPill();
    initMoneyFormatting(document);
  });
} else {
  ScrollPill.init(document);
  if (!window.__pageScrollPill) window.__pageScrollPill = new PageScrollPill();
  initMoneyFormatting(document);
}

window.addEventListener(
  'resize',
  debounce(() => {
    ScrollPill.init(document);
  }, 100)
);
document.addEventListener('shopify:section:load', (event) => {
  ScrollPill.init(event.target);
  initMoneyFormatting(event.target);
});

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.syncQuantitySummary(target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      // Handle card-style variant radios (name="id") inside variant-selects before
      // standard option radios. These cards do not live inside .product-form__input.
      if (target.name === 'id') {
        const container = this.querySelector('.variants-container');
        if (!container) return;

        const labels = container.querySelectorAll('label.variant-option');
        labels.forEach((label) => {
          const input = label.querySelector('input[type="radio"]');
          const isSelected = input === target;
          label.classList.toggle('selected', isSelected);
          label.setAttribute('aria-pressed', String(isSelected));
          label.setAttribute('aria-selected', String(isSelected));
        });
        return;
      }

      const productFormInput = target.closest(`.product-form__input`);
      const selectedSwatchValue = productFormInput?.querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  syncQuantitySummary(input) {
    if (input?.name !== 'id') return;

    const title = input.closest('label.variant-option')?.querySelector('.variant-title')?.textContent || '';
    const shotsPerPack = Number(input.dataset.shotsPerPack || title.match(/\b(6|12|18|30)\b/)?.[1] || 0);
    const productInfo = this.closest('product-info');
    const summary = productInfo?.querySelector('[data-vj-quantity-summary]');
    const total = summary?.querySelector('[data-vj-total-shots]');
    if (!summary || !total || shotsPerPack <= 0) return;

    const quantity = Math.max(Number(productInfo.querySelector('.quantity__input')?.value || 1), 1);
    summary.dataset.shotsPerPack = String(shotsPerPack);
    total.textContent = `${quantity * shotsPerPack} shots en total`;
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
// Hydrate per-card CSS variables from data attributes for variant cards
// This allows each card to have its own --card-primary/--card-dot if provided
function hydrateVariantCardVars(root = document) {
  try {
    const scope = root instanceof HTMLElement ? root : document;
    scope.querySelectorAll('label.variant-option').forEach((label) => {
      const primary = label.dataset.primaryColor;
      const dot = label.dataset.dotColor;
      if (primary) {
        label.style.setProperty('--card-primary', primary);
        label.style.setProperty('--card-border-selected', primary);
      }
      if (dot) {
        label.style.setProperty('--card-dot', dot);
      }
    });
  } catch (e) {
    // no-op
  }
}

document.addEventListener('DOMContentLoaded', () => hydrateVariantCardVars());
document.addEventListener('product-info:loaded', (e) => hydrateVariantCardVars(e.target || document));

function hydrateDeliveryEstimate(root = document) {
  const scope = root instanceof HTMLElement ? root : document;
  const nodes = [];
  if (scope instanceof Element && scope.matches('[data-vj-delivery-estimate]')) nodes.push(scope);
  scope.querySelectorAll('[data-vj-delivery-estimate]').forEach((node) => nodes.push(node));
  if (!nodes.length) return;

  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let added = 0;

    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }

    return result;
  };

  const dateFormatter = new Intl.DateTimeFormat('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const today = new Date();
  const start = addBusinessDays(today, 1);
  const end = addBusinessDays(today, 3);
  const text = `Entrega estimada: ${dateFormatter.format(start)} a ${dateFormatter.format(end)}`;

  nodes.forEach((node) => {
    const textTarget = node.querySelector('[data-vj-delivery-estimate-text]') || node;
    textTarget.textContent = text;
  });
}

document.addEventListener('DOMContentLoaded', () => hydrateDeliveryEstimate());
document.addEventListener('product-info:loaded', (e) => hydrateDeliveryEstimate(e.target || document));
document.addEventListener('shopify:section:load', (e) => hydrateDeliveryEstimate(e.target || document));

if (typeof MutationObserver !== 'undefined') {
  let deliveryEstimateFrame = null;
  const pendingDeliveryRoots = new Set();
  const deliveryEstimateObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches('[data-vj-delivery-estimate]') || node.querySelector('[data-vj-delivery-estimate]')) {
          pendingDeliveryRoots.add(node);
        }
      });
    });

    if (!pendingDeliveryRoots.size || deliveryEstimateFrame) return;
    deliveryEstimateFrame = requestAnimationFrame(() => {
      pendingDeliveryRoots.forEach((root) => {
        if (root.isConnected) hydrateDeliveryEstimate(root);
      });
      pendingDeliveryRoots.clear();
      deliveryEstimateFrame = null;
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (document.body) {
      deliveryEstimateObserver.observe(document.body, { childList: true, subtree: true });
    }
  });
}

/*
 * iOS Safari can deliver a horizontal touch sequence to the correct carousel
 * while leaving its native scroll position frozen when the gesture begins in
 * the lower part of the visual viewport. Preserve native scrolling whenever
 * it starts normally and move the rail only when Safari has stayed at the
 * exact starting position through a clearly horizontal gesture.
 */
(() => {
  const mobileViewport = window.matchMedia('(max-width: 989px)');
  let gesture = null;
  let dragFrame = null;
  let settleFrame = null;
  let settlingGesture = null;

  const findHorizontalScroller = (target) => {
    let element = target instanceof Element ? target : null;

    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      const canScrollHorizontally =
        element.scrollWidth > element.clientWidth + 2
        && (style.overflowX === 'auto' || style.overflowX === 'scroll');

      if (canScrollHorizontally) return element;
      element = element.parentElement;
    }

    return null;
  };

  const clampScroll = (rail, value) => {
    const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);
    return Math.min(maxScroll, Math.max(0, value));
  };

  const stopAnimations = () => {
    if (dragFrame) window.cancelAnimationFrame(dragFrame);
    if (settleFrame) window.cancelAnimationFrame(settleFrame);
    dragFrame = null;
    settleFrame = null;
  };

  const restoreScrollBehavior = (activeGesture) => {
    if (!activeGesture?.rail) return;

    if (activeGesture.inlineScrollBehavior) {
      activeGesture.rail.style.scrollBehavior = activeGesture.inlineScrollBehavior;
    } else {
      activeGesture.rail.style.removeProperty('scroll-behavior');
    }
  };

  const renderDrag = () => {
    dragFrame = null;
    if (!gesture || gesture.mode !== 'fallback') return;
    gesture.rail.scrollLeft = gesture.targetLeft;
  };

  const getCardPositions = (rail) => {
    const snapCards = Array.from(rail.querySelectorAll('*')).filter((element) => {
      const rect = element.getBoundingClientRect();
      return (
        rect.width > 24
        && rect.height > 10
        && window.getComputedStyle(element).scrollSnapAlign !== 'none'
      );
    });
    const directCards = Array.from(rail.children).filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 24 && rect.height > 10;
    });
    const cards = snapCards.length > 1 ? snapCards : directCards;

    if (cards.length < 2) return [];

    const railRect = rail.getBoundingClientRect();
    const rawPositions = cards.map((card) => {
      const rect = card.getBoundingClientRect();
      return rail.scrollLeft + rect.left - railRect.left;
    });
    const firstPosition = rawPositions[0];

    return rawPositions.map((position) => clampScroll(rail, position - firstPosition));
  };

  const settleToAdjacentCard = (completedGesture) => {
    const positions = getCardPositions(completedGesture.rail);
    if (positions.length < 2 || Math.abs(completedGesture.deltaX) < 24) {
      restoreScrollBehavior(completedGesture);
      return;
    }

    const startIndex = positions.reduce(
      (closestIndex, position, index) =>
        Math.abs(position - completedGesture.startLeft)
          < Math.abs(positions[closestIndex] - completedGesture.startLeft)
          ? index
          : closestIndex,
      0
    );
    const direction = completedGesture.deltaX > 0 ? 1 : -1;
    const targetIndex = Math.min(positions.length - 1, Math.max(0, startIndex + direction));
    const startPosition = completedGesture.rail.scrollLeft;
    const targetPosition = positions[targetIndex];
    const distance = targetPosition - startPosition;

    if (Math.abs(distance) < 1) {
      completedGesture.rail.scrollLeft = targetPosition;
      restoreScrollBehavior(completedGesture);
      return;
    }

    const duration = Math.min(440, Math.max(280, 260 + Math.abs(distance) * 0.25));
    const startTime = performance.now();
    settlingGesture = completedGesture;

    const animate = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 4);
      completedGesture.rail.scrollLeft = startPosition + distance * easedProgress;

      if (progress >= 1) {
        completedGesture.rail.scrollLeft = targetPosition;
        settleFrame = null;
        restoreScrollBehavior(completedGesture);
        settlingGesture = null;
        return;
      }

      settleFrame = window.requestAnimationFrame(animate);
    };

    settleFrame = window.requestAnimationFrame(animate);
  };

  document.addEventListener(
    'touchstart',
    (event) => {
      stopAnimations();
      restoreScrollBehavior(settlingGesture);
      settlingGesture = null;
      restoreScrollBehavior(gesture);
      gesture = null;
      if (!mobileViewport.matches || event.touches.length !== 1) return;

      const rail = findHorizontalScroller(event.target);
      if (!rail) return;

      gesture = {
        rail,
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        startLeft: rail.scrollLeft,
        mode: 'pending',
        targetLeft: rail.scrollLeft,
        deltaX: 0,
        deltaY: 0,
        inlineScrollBehavior: rail.style.scrollBehavior,
      };
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!gesture || event.touches.length !== 1) return;

      const deltaX = gesture.startX - event.touches[0].clientX;
      const deltaY = gesture.startY - event.touches[0].clientY;
      gesture.deltaX = deltaX;
      gesture.deltaY = deltaY;

      if (
        gesture.mode === 'pending'
        && Math.abs(deltaX) > 8
        && Math.abs(deltaX) > Math.abs(deltaY) * 1.15
      ) {
        gesture.mode =
          Math.abs(gesture.rail.scrollLeft - gesture.startLeft) > 2
            ? 'native'
            : 'fallback';

        if (gesture.mode === 'fallback') {
          gesture.rail.style.scrollBehavior = 'auto';
        }
      }

      if (gesture.mode !== 'fallback') return;

      gesture.targetLeft = clampScroll(gesture.rail, gesture.startLeft + deltaX);

      if (!dragFrame) dragFrame = window.requestAnimationFrame(renderDrag);
    },
    { capture: true, passive: true }
  );

  const clearGesture = () => {
    if (!gesture) return;

    const completedGesture = gesture;
    gesture = null;

    if (dragFrame) {
      window.cancelAnimationFrame(dragFrame);
      dragFrame = null;
    }

    if (completedGesture.mode === 'fallback') {
      completedGesture.rail.scrollLeft = completedGesture.targetLeft;
    }

    if (
      completedGesture.mode === 'fallback'
      || completedGesture.mode === 'native'
    ) {
      completedGesture.rail.style.scrollBehavior = 'auto';
      settleToAdjacentCard(completedGesture);
    } else {
      restoreScrollBehavior(completedGesture);
    }
  };

  document.addEventListener('touchend', clearGesture, { capture: true, passive: true });
  document.addEventListener('touchcancel', clearGesture, { capture: true, passive: true });
})();

/* Keep Pack Ritual navigation on the current sellable variant. This lives in
 * the base bundle so cached storefront documents receive the fix as well. */
(() => {
  const ritualPath = '/products/pack-de-3';
  const ritualVariantId = '51478368125226';

  const normalizeRitualLink = (target) => {
    const link = target?.closest?.('a[href]');
    if (!link) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== ritualPath || url.searchParams.has('variant')) return;

    url.searchParams.set('variant', ritualVariantId);
    link.href = `${url.pathname}${url.search}${url.hash}`;
  };

  const normalizeExistingLinks = () => {
    document.querySelectorAll(`a[href^="${ritualPath}"]`).forEach(normalizeRitualLink);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalizeExistingLinks, { once: true });
  } else {
    normalizeExistingLinks();
  }

  document.addEventListener('pointerdown', (event) => normalizeRitualLink(event.target), true);
  document.addEventListener('focusin', (event) => normalizeRitualLink(event.target), true);
  document.addEventListener('click', (event) => normalizeRitualLink(event.target), true);
})();
